// src/components/RequiredConsentsDialog.jsx
// When required consents are missing, redirect to /welcome so the user sees
// the existing Welcome page with DocxViewer and all docs hooked up.
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { useRequiredConsents } from '@/hooks/useConsents';

// Roles that must have required consents before using the app
const ROLES_REQUIRING_CONSENTS = ['teacher', 'branch_admin', 'super_admin', 'parent'];
// Admin roles require terms + DPA + privacy; teachers/parents only privacy
const ROLES_REQUIRING_ALL_THREE = ['branch_admin', 'super_admin'];

export default function RequiredConsentsDialog() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const consentUserId = userProfile?.id ?? user?.id;
  const { data: consentsStatus, isLoading, isFetching } = useRequiredConsents(consentUserId);

  useEffect(() => {
    if (!user || isLoading || isFetching || !consentsStatus) return;
    if (location.pathname === '/welcome') return;

    const role = userProfile?.active_role ?? userProfile?.primary_role ?? userProfile?.roles?.[0];
    if (!role || !ROLES_REQUIRING_CONSENTS.includes(role)) return;

    const isAdmin = ROLES_REQUIRING_ALL_THREE.includes(role);
    const allGranted = isAdmin
      ? consentsStatus.hasTermsOfService &&
        consentsStatus.hasPrivacyPolicy &&
        consentsStatus.hasDataProcessing
      : consentsStatus.hasPrivacyPolicy;

    if (!allGranted) {
      navigate('/welcome', { replace: true });
    }
  }, [user, userProfile, consentUserId, consentsStatus, isLoading, isFetching, location.pathname, navigate]);

  return null;
}
